/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module LogsModule */

import { ComponentStore } from '@ngrx/component-store';

import {
    Observable,
    Subject,
    Subscription,
} from 'rxjs';

import { DevLoggerService } from 'dev-logger-service';

import {
    ILogsDefaultSignpostData,
    ILogsSelectedSignpostConfig,
    ILogsSignpostUpdatableParams,
    TLogsSignpostApiResponseData,
    TLogsSignpostStoreData,
} from '../logs.types';

import { ILogsStates, LogsStore } from './logs.store';
import { transformSignpostData } from '../utils/logs-signpost.utils';

/**
 * @param hasError - Inform when signpost has an error.
 * @param isTimedOut - Inform if one of the requests has timed out.
 */
type TErrorStates = {
    hasError: boolean;
    isTimedOut: boolean;
};

/**
 * @param logsSignpostData - Response from backend transfomed to be utilised on UI for displaying.
 * @param selectedSignpostConfig - Selected signpost name and its respective config key for mapping.
 * @param selectedSignpostRequestIds - Selected signpost request ids, need it for
 *  cancelling ongoing request while closing signpost.
 * @param isLoading - Flag to indicate whether a request is in progress.
 * @param errorStates - state properties pertaining to errors and timeouts.
 */
export interface ILogsSignpostStates {
    logsSignpostData: TLogsSignpostStoreData;
    selectedSignpostConfig: ILogsSelectedSignpostConfig;
    selectedSignpostRequestIds: string[];
    isLoading: boolean;
    errorStates: TErrorStates;
}

/**
 * @description State and Effect management for LogsSignpostComponent.
 * @author Suraj Kumar, Yogesh Patil
 */
export abstract class LogsSignpostStore <T extends ILogsSignpostStates> extends ComponentStore<T> {
    // ********************************** Selectors **********************************

    public readonly logsDefaultSignpostData$ = this.select(
        state => state.logsSignpostData as ILogsDefaultSignpostData[],
    );

    public readonly hasNoData$ = this.select(
        state => {
            // Signpost data after transformation can be either object or array.
            const hasNoData = state.logsSignpostData ?
                !Object.keys(state.logsSignpostData).length : true;

            return hasNoData && !state.isLoading;
        },
    );

    public readonly selectedSignpostRequestIds$ = this.select(
        state => state.selectedSignpostRequestIds,
    );

    public readonly selectedSignpost$ = this.select(state => state.selectedSignpostConfig);

    public readonly isLoading$ = this.select(state => state.isLoading);

    public readonly hasError$ = this.select(state => state.errorStates.hasError);

    public readonly isTimedOut$ = this.select(state => state.errorStates.isTimedOut);

    public readonly logSelectedSignpostConfigKey$ = this.select(
        state => state.selectedSignpostConfig?.configKey,
    );

    /**
     * Selector for circumstances under which to show the empty data message.
     */
    public readonly showEmptyDataMessage$ = this.select(
        this.hasError$,
        this.hasNoData$,
        (hasError, hasNoData) => {
            return hasNoData && !hasError;
        },
    );

    /**
     * Selector for circumstances under which to show the error message.
     */
    public readonly showErrorMessage$ = this.select(
        this.hasError$,
        this.isLoading$,
        (hasError, isLoading) => {
            return hasError && !isLoading;
        },
    );

    /**
     * Selector for circumstances under which to show the timeout message.
     */
    public readonly showTimeoutMessage$ = this.select(
        this.isTimedOut$,
        this.logsStore.showTimeoutMessage$,
        this.isLoading$,
        this.hasError$,
        (isTimedOut, logsPageShowTimeoutMessage, isLoading, hasError) => {
            return isTimedOut && !isLoading && !hasError && !logsPageShowTimeoutMessage;
        },
    );

    // ********************************** Effects **********************************

    /**
     * Request selected signpost data and update the state accordingly.
     */
    public readonly getLogSignpostData: (
        signpostParams: ILogsSignpostUpdatableParams,
    ) => Subscription;

    /**
     * Subject to reset placement of the signpost.
     */
    public readonly overlaySizeChangeSubject$ = new Subject<void>();

    // ********************************** Updaters **********************************

    public readonly setLogSignpostGroupby = this.updater(
        (state, selectedSignpostConfig: ILogsSelectedSignpostConfig) => ({
            ...state,
            selectedSignpostConfig,
        }),
    );

    private readonly setDataIsLoading = this.updater((state, isLoading: boolean) => ({
        ...state,
        isLoading,
    }));

    private readonly setHasError = this.updater((state, hasError: boolean) => ({
        ...state,
        errorStates: {
            ...state.errorStates,
            hasError,
        },
    }));

    private readonly setSignpostData = this.updater((
        state,
        logsSignpostData: TLogsSignpostStoreData,
    ) => ({
        ...state,
        logsSignpostData,
    }));

    private readonly setIsTimedOut = this.updater((state, isTimedOut: boolean) => ({
        ...state,
        errorStates: {
            ...state.errorStates,
            isTimedOut,
        },
    }));

    private initialState: T;

    // ****************************** Abstract Properties ****************************

    /**
     * The request ID for the log signpost request. Should be overridden by the concrete store.
     */
    protected abstract logSignpostRequestId: string;

    /**
     * Subject to close the signpost. Should be overridden by the concrete store.
     */
    public abstract readonly closeSignpost$: Observable<void>;

    // ********************************** Constructor **********************************

    constructor(
        initialState: T,
        protected readonly logsStore: LogsStore<ILogsStates>,
        protected readonly devLoggerService: DevLoggerService,
    ) {
        super(initialState);
        this.initialState = initialState;
    }

    // ********************************** Methods **********************************

    /**
     * Close the signpost.
     * Should be overridden by a concrete class.
     */
    public abstract closeSignpost(): void;

    /**
     * Set signpost state to default state.
     * Should be overridden by a concrete class.
     */
    public abstract setDefaultState(): void;

    /**
     * Return an observable to inform subscriptions of a change in the data, will reset
     * placement of the signpost.
     */
    public get overlaySizeChange$(): Observable<void> {
        return this.overlaySizeChangeSubject$.asObservable();
    }

    /**
     * Set isLoading to true upon initiating API request.
     */
    protected startLoading = (): void => {
        this.setDataIsLoading(true);
        this.setHasError(false);
        this.setIsTimedOut(false);
    };

    /**
     * Set isLoading to false.
     */
    protected stopLoading = (): void => {
        this.setDataIsLoading(false);
    };

    /**
     * Update state with results once data is returned from API.
     */
    protected handleApiData = (data: TLogsSignpostApiResponseData): void => {
        this.setState(state => {
            const logsSignpostData =
                transformSignpostData(data, state.selectedSignpostConfig?.configKey);

            this.overlaySizeChangeSubject$.next();

            return {
                ...state,
                logsSignpostData,
            };
        });
    };

    /**
     * Handle API errors.
     */
    protected handleApiError = (err: string): void => {
        this.devLoggerService.error(err);
        this.setDataIsLoading(false);
        this.setSignpostData(this.initialState.logsSignpostData);
        this.setIsTimedOut(false);
        this.setHasError(true);
    };

    /**
     * Set isTimedOut when API is timed out.
     */
    protected handleApiLoopComplete = (isTimedOut: boolean): void => {
        if (isTimedOut) {
            this.setIsTimedOut(true);
        }
    };
}
